import React from 'react';
import { Star, Target, CheckCircle, TrendingUp } from 'lucide-react';

// Helper function for smart text truncation that respects word boundaries
const smartTruncate = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  
  const truncated = text.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  // If there's a space within the last 10 characters, cut at the word boundary
  if (lastSpaceIndex > maxLength - 10) {
    return truncated.substring(0, lastSpaceIndex);
  }
  
  // Otherwise, just truncate at the character limit
  return truncated;
};

const EnhancedOverallAssessment = ({ overallAssessment, annotations, essayText }) => {
  if (!overallAssessment) return null;

  // Error boundary wrapper
  try {

  // Generate explanations for scores based on annotations and content analysis
  const generateScoreExplanation = (scoreType, score) => {
    const getScoreLevel = (score) => score >= 8 ? 'high' : score >= 6 ? 'medium' : 'low';
    const scoreLevel = getScoreLevel(score);
    
    const explanations = {
      uniquenessScore: {
        high: {
          explanation: "Your essay demonstrates a highly distinctive perspective and experiences that set you apart from other applicants.",
          evidence: getUniqueElements(annotations, essayText),
          next: score < 10 ? "Consider adding one more unique detail that only you could share." : null
        },
        medium: {
          explanation: "Your essay has some unique elements, but could benefit from more distinctive details.",
          evidence: getGenericElements(annotations),
          next: "Look for moments that only you experienced - specific details, unusual perspectives, or uncommon insights."
        },
        low: {
          explanation: "Your essay currently reads like it could apply to many students. Focus on what makes your story uniquely yours.",
          evidence: getGenericElements(annotations),
          next: "Identify 2-3 specific details, thoughts, or reactions that only you would have had in this situation."
        }
      },
      authenticityScore: {
        high: {
          explanation: "Your voice feels genuine and the experiences come across as believable and heartfelt.",
          evidence: getAuthenticElements(annotations, essayText),
          next: score < 10 ? "Consider sharing one more vulnerable or honest moment to deepen authenticity." : null
        },
        medium: {
          explanation: "Your essay has authentic moments but some sections feel more polished than genuine.",
          evidence: getInauthenticElements(annotations),
          next: "Write more conversationally - imagine telling this story to a close friend."
        },
        low: {
          explanation: "The essay feels overly polished or generic. Readers want to hear your real voice.",
          evidence: getInauthenticElements(annotations),
          next: "Rewrite sections to sound more like how you actually speak and think."
        }
      },
      impactScore: {
        high: {
          explanation: "Your essay will likely be memorable to admissions officers and clearly demonstrates your potential contribution.",
          evidence: getImpactfulElements(annotations, essayText),
          next: score < 10 ? "Consider strengthening your conclusion to leave an even stronger final impression." : null
        },
        medium: {
          explanation: "Your essay makes a reasonable impression but may not stand out significantly in the applicant pool.",
          evidence: getWeakImpactElements(annotations),
          next: "Focus on the transformation or insight - what changed in you through this experience?"
        },
        low: {
          explanation: "Your essay may not make a strong enough impression on admissions officers.",
          evidence: getWeakImpactElements(annotations),
          next: "Emphasize the stakes, the growth, or the lasting change this experience created in you."
        }
      }
    };

    return explanations[scoreType]?.[scoreLevel] || { explanation: "Score analysis not available", evidence: [], next: null };
  };

  // Helper functions to extract evidence from annotations
  const getUniqueElements = (annotations, text) => {
    const uniqueAnnotations = annotations.filter(a => 
      a.type === 'highlight' && 
      (a.feedback?.toLowerCase().includes('unique') || 
       a.feedback?.toLowerCase().includes('distinctive') ||
       a.feedback?.toLowerCase().includes('original'))
    );
    
    return uniqueAnnotations.length > 0 ? 
      uniqueAnnotations.map(a => `"${smartTruncate(a.originalText)}..." - ${a.feedback}`) :
      ['Specific unique details could be strengthened'];
  };

  const getGenericElements = (annotations) => {
    const genericAnnotations = annotations.filter(a => 
      a.feedback?.toLowerCase().includes('generic') || 
      a.feedback?.toLowerCase().includes('common') ||
      a.feedback?.toLowerCase().includes('cliché')
    );
    
    return genericAnnotations.length > 0 ?
      genericAnnotations.map(a => `"${smartTruncate(a.originalText)}..." - ${a.feedback}`) :
      ['Look for opportunities to add more specific, personal details'];
  };

  const getAuthenticElements = (annotations, text) => {
    const authenticity = annotations.filter(a => 
      a.feedback?.toLowerCase().includes('authentic') ||
      a.feedback?.toLowerCase().includes('genuine') ||
      a.feedback?.toLowerCase().includes('voice')
    );
    
    return authenticity.length > 0 ?
      authenticity.map(a => `"${smartTruncate(a.originalText)}..." - ${a.feedback}`) :
      ['Your natural voice comes through well in most sections'];
  };

  const getInauthenticElements = (annotations) => {
    const inauthentic = annotations.filter(a => 
      a.feedback?.toLowerCase().includes('stiff') ||
      a.feedback?.toLowerCase().includes('formal') ||
      a.feedback?.toLowerCase().includes('natural')
    );
    
    return inauthentic.length > 0 ?
      inauthentic.map(a => `"${smartTruncate(a.originalText)}..." - ${a.feedback}`) :
      ['Consider making the tone more conversational'];
  };

  const getImpactfulElements = (annotations, text) => {
    const impact = annotations.filter(a => 
      a.feedback?.toLowerCase().includes('powerful') ||
      a.feedback?.toLowerCase().includes('compelling') ||
      a.feedback?.toLowerCase().includes('strong')
    );
    
    return impact.length > 0 ?
      impact.map(a => `"${smartTruncate(a.originalText)}..." - ${a.feedback}`) :
      ['Strong emotional resonance throughout the essay'];
  };

  const getWeakImpactElements = (annotations) => {
    const weak = annotations.filter(a => 
      a.feedback?.toLowerCase().includes('weak') ||
      a.feedback?.toLowerCase().includes('develop') ||
      a.severity === 'major'
    );
    
    return weak.length > 0 ?
      weak.map(a => `"${smartTruncate(a.originalText)}..." needs: ${a.feedback}`) :
      ['Consider adding more emotional depth or stakes to key moments'];
  };

  const uniqueAnalysis = generateScoreExplanation('uniquenessScore', overallAssessment.uniquenessScore);
  const authenticAnalysis = generateScoreExplanation('authenticityScore', overallAssessment.authenticityScore);
  const impactAnalysis = generateScoreExplanation('impactScore', overallAssessment.impactScore);

  const ScoreSection = ({ title, score, analysis, icon: Icon, color }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <h4 className="font-semibold text-stone-800">{title}</h4>
        <div className={`text-2xl font-bold ${
          score >= 8 ? 'text-emerald-600' : score >= 6 ? 'text-yellow-600' : 'text-red-600'
        }`}>
          {score}/10
        </div>
      </div>
      
      <div className="bg-white p-3 rounded border border-stone-200">
        <p className="text-sm text-stone-700 mb-2">{analysis.explanation}</p>
        
        {analysis.evidence.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Evidence:</p>
            <ul className="space-y-1">
              {analysis.evidence.slice(0, 2).map((evidence, i) => (
                <li key={i} className="text-xs text-stone-600 pl-2 border-l-2 border-stone-200">
                  {evidence}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {analysis.next && (
          <div className="mt-3 p-2 bg-azure-50 rounded border border-azure-200">
            <p className="text-xs font-semibold text-azure-800 mb-1">Next Step:</p>
            <p className="text-xs text-azure-700">{analysis.next}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-azure-50 border border-azure-300 rounded-lg p-4"
         style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
      <div className="flex items-start gap-2 mb-4">
        <Star className="w-4 h-4 text-azure-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-azure-800 mb-3">Overall Assessment</h3>
          
          <div className="space-y-4">
            <ScoreSection
              title="Uniqueness"
              score={overallAssessment.uniquenessScore}
              analysis={uniqueAnalysis}
              icon={Star}
              color="text-violet-500"
            />
            
            <ScoreSection
              title="Authenticity"
              score={overallAssessment.authenticityScore}
              analysis={authenticAnalysis}
              icon={CheckCircle}
              color="text-emerald-500"
            />
            
            <ScoreSection
              title="Impact"
              score={overallAssessment.impactScore}
              analysis={impactAnalysis}
              icon={Target}
              color="text-azure-500"
            />
          </div>

          {/* Overall improvement summary */}
          {(overallAssessment.uniquenessScore < 10 || 
            overallAssessment.authenticityScore < 10 || 
            overallAssessment.impactScore < 10) && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-yellow-600" />
                <h4 className="text-sm font-semibold text-yellow-800">Priority Focus Areas</h4>
              </div>
              <div className="space-y-1 text-xs text-yellow-700">
                {overallAssessment.uniquenessScore < 8 && (
                  <div>• Add more distinctive, personal details that only you could share</div>
                )}
                {overallAssessment.authenticityScore < 8 && (
                  <div>• Write in a more natural, conversational tone</div>
                )}
                {overallAssessment.impactScore < 8 && (
                  <div>• Strengthen the emotional impact and transformation</div>
                )}
              </div>
            </div>
          )}

          {/* First impression note if available */}
          {overallAssessment.firstImpressionImpact && (
            <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
              <h4 className="text-sm font-semibold text-indigo-800 mb-1">First Impression Impact:</h4>
              <p className="text-sm text-indigo-700 italic">"{overallAssessment.firstImpressionImpact}"</p>
            </div>
          )}

          {/* Memorability factor if available */}
          {overallAssessment.memorabilityFactor && (
            <div className="mt-3 p-3 bg-violet-50 border border-violet-200 rounded">
              <h4 className="text-sm font-semibold text-violet-800 mb-1">Memorability:</h4>
              <p className="text-sm text-violet-700">{overallAssessment.memorabilityFactor}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  } catch (error) {
    console.error('Error rendering EnhancedOverallAssessment:', error);
    
    // Fallback UI
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg p-4"
           style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0">⚠️</div>
          <div className="flex-1">
            <h3 className="text-xs font-semibold text-red-800 mb-2">Assessment Temporarily Unavailable</h3>
            <p className="text-sm text-red-700">
              Unable to display detailed assessment. Basic scores may still be available in other sections.
            </p>
            {overallAssessment && (
              <div className="mt-2 text-xs text-red-600">
                Scores: Uniqueness {overallAssessment.uniquenessScore || 'N/A'}/10, 
                Authenticity {overallAssessment.authenticityScore || 'N/A'}/10, 
                Impact {overallAssessment.impactScore || 'N/A'}/10
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default EnhancedOverallAssessment;